import React, { useEffect, useMemo, useState } from "react"
import { FaEdit, FaPlus, FaSpinner, FaToggleOff, FaToggleOn, FaTrash, FaWarehouse, FaBoxes, FaRulerCombined, FaLayerGroup, FaTags, FaCog, FaTruck, FaStore } from "react-icons/fa"
import { FiX } from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import api from "../../libs/axios"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import { useDebouncedCallback } from "../../hooks/useDebounce"
import { ConfirmModal } from "../../components/ConfirmModal"
import { SelectWithSearch, type SelectOption } from "../../utils/select-with-search"
import {
  Cell,
  DataTable,
  FilterField,
  FilterRow,
  IconAction,
  ListCard,
  PrimaryButton,
  RefreshButton,
  SearchBox,
  Select,
  Th,
} from "./shared/MaterialUI"

export interface MaterialField {
  name: string
  label: string
  type: "text" | "number" | "textarea" | "select" | "checkbox" | "select-search"
  options?: Array<{ label: string; value: string | number }>
  /** For type "select-search": the materials sub-path to query, e.g. "classification-categories" */
  optionsEndpoint?: string
  /** For type "select-search": the field on each option row used as the label (default "name") */
  optionLabelKey?: string
  /** For type "select-search": the association key on a row that carries the label object on edit, e.g. "classification_category" */
  displayField?: string
}

export interface MaterialMasterConfig {
  title: string
  endpoint: string
  resource: string
  fields: MaterialField[]
  columns: Array<{ key: string; label: string }>
  primaryLabel: string
}

const iconMap: Record<string, React.ComponentType<any>> = {
  warehouses: FaWarehouse,
  items: FaBoxes,
  units: FaRulerCombined,
  "classification-categories": FaTags,
  "classification-subcategories": FaLayerGroup,
  "cost-categories": FaCog,
  suppliers: FaStore,
  transporters: FaTruck,
}

const BOOLEAN_KEYS = new Set(["is_active", "billable", "decimal_allowed"])

// Resolve a possibly dotted column key against a row (e.g. "classification_category.name")
function getCellValue(row: any, key: string): any {
  if (!key.includes(".")) return row?.[key]
  return key.split(".").reduce((acc, part) => (acc == null ? acc : acc[part]), row)
}

export function MaterialMasterPage({ config }: { config: MaterialMasterConfig }) {
  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType>({ offset: 0, page: 1, limit: 10, total_pages: 0, total_count: 0, has_next_page: false, has_prev_page: false, next_page: null, prev_page: null })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showView, setShowView] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [selected, setSelected] = useState<any | null>(null)
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [selectValues, setSelectValues] = useState<Record<string, SelectOption | null>>({})
  const [actionLoading, setActionLoading] = useState(false)

  const debouncedFetch = useDebouncedCallback((value: string) => {
    fetchRows(1, value, statusFilter)
  }, 350)

  const fetchRows = async (page = 1, searchValue = search, status = statusFilter, limit = pagination.limit) => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { page, limit }
      if (searchValue.trim()) params.search = searchValue.trim()
      if (status) params.is_active = status
      const response = await api.get(`/materials/${config.endpoint}`, { params })
      setRows(response.data?.data?.items || [])
      setPagination(response.data?.data?.pagination || pagination)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchRows(1) }, [])

  const buildInitialOption = (row: any, field: MaterialField): SelectOption | null => {
    const id = row?.[field.name]
    if (id === null || id === undefined || id === "") return null
    const assoc = field.displayField ? row?.[field.displayField] : null
    const label = assoc?.[field.optionLabelKey || "name"] ?? `#${id}`
    return { id: Number(id), value: Number(id), label: String(label) }
  }

  const buildSelectValues = (row: any | null) => {
    const next: Record<string, SelectOption | null> = {}
    config.fields.forEach((field) => {
      if (field.type === "select-search") next[field.name] = row ? buildInitialOption(row, field) : null
    })
    return next
  }

  const openAdd = () => {
    setSelected(null)
    setFormData({})
    setSelectValues(buildSelectValues(null))
    setShowAdd(true)
  }

  const openEdit = (row: any) => {
    setSelected(row)
    setFormData(row)
    setSelectValues(buildSelectValues(row))
    setShowEdit(true)
  }

  const openView = (row: any) => {
    setSelected(row)
    setShowView(true)
  }

  const closeDrawer = () => {
    if (actionLoading) return
    setShowAdd(false)
    setShowEdit(false)
  }

  const submitForm = async () => {
    setActionLoading(true)
    try {
      const payload = { ...formData }
      if (selected?.id && showEdit) {
        await api.put(`/materials/${config.endpoint}/${selected.id}`, payload)
      } else {
        await api.post(`/materials/${config.endpoint}`, payload)
      }
      setShowAdd(false)
      setShowEdit(false)
      setSelected(null)
      await fetchRows(pagination.page)
    } finally {
      setActionLoading(false)
    }
  }

  const deleteRow = async () => {
    if (!selected?.id) return
    setActionLoading(true)
    try {
      await api.delete(`/materials/${config.endpoint}/${selected.id}`)
      setConfirmDelete(false)
      setSelected(null)
      await fetchRows(pagination.page)
    } finally {
      setActionLoading(false)
    }
  }

  const toggleStatus = async (row: any) => {
    setActionLoading(true)
    try {
      await api.patch(`/materials/${config.endpoint}/${row.id}/status`, { is_active: !row.is_active })
      await fetchRows(pagination.page)
    } finally {
      setActionLoading(false)
    }
  }

  const TitleIcon = useMemo(() => iconMap[config.endpoint] || FaBoxes, [config.endpoint])

  const renderCell = (row: any, key: string) => {
    if (BOOLEAN_KEYS.has(key)) {
      const active = !!row[key]
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}>
          {active ? "Yes" : "No"}
        </span>
      )
    }
    const value = getCellValue(row, key)
    return <span className="text-sm text-gray-900">{value === null || value === undefined || value === "" ? "N/A" : String(value)}</span>
  }

  const renderField = (field: MaterialField) => {
    if (field.type === "textarea") {
      return (
        <textarea
          value={formData[field.name] ?? ""}
          onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        />
      )
    }
    if (field.type === "select") {
      return (
        <select
          value={formData[field.name] ?? ""}
          onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
        >
          <option value="">Select</option>
          {field.options?.map((opt) => (
            <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )
    }
    if (field.type === "select-search") {
      return (
        <SelectWithSearch
          placeholder={`Search ${field.label.toLowerCase()}...`}
          value={selectValues[field.name] ?? null}
          onChange={(opt) => {
            setSelectValues((prev) => ({ ...prev, [field.name]: opt }))
            setFormData((prev) => ({ ...prev, [field.name]: opt ? opt.value : null }))
          }}
          apiEndpoint={`/materials/${field.optionsEndpoint}`}
          dataKey="items"
          labelKey={field.optionLabelKey || "name"}
          valueKey="id"
          minSearchLength={0}
          className="w-full"
        />
      )
    }
    if (field.type === "checkbox") {
      return (
        <label className="inline-flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            checked={!!formData[field.name]}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.checked }))}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Enabled</span>
        </label>
      )
    }
    return (
      <input
        type={field.type}
        value={formData[field.name] ?? ""}
        onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
      />
    )
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<TitleIcon className="w-5 h-5" />}
        title={config.title}
        subtitle={`Manage ${config.title.toLowerCase()} master · ${pagination.total_count} record${pagination.total_count !== 1 ? "s" : ""}`}
        actions={
          <>
            <RefreshButton loading={loading} onClick={() => fetchRows(pagination.page)} />
            <PrimaryButton onClick={openAdd}>
              <FaPlus className="w-4 h-4" /> Add {config.title}
            </PrimaryButton>
          </>
        }
        filters={
          <FilterRow>
            <FilterField label="Search" grow>
              <SearchBox
                value={search}
                onChange={(value) => {
                  setSearch(value)
                  debouncedFetch(value)
                }}
                placeholder={`Search ${config.title.toLowerCase()}...`}
              />
            </FilterField>
            <FilterField label="Status">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  fetchRows(1, search, e.target.value)
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </FilterField>
          </FilterRow>
        }
        footer={
          <Pagination
            pagination={pagination}
            onPageChange={(page) => fetchRows(page)}
            onLimitChange={(limit) => fetchRows(1, search, statusFilter, limit)}
          />
        }
      >
        <DataTable
          colSpan={config.columns.length + 1}
          loading={loading}
          empty={rows.length === 0}
          loadingText={`Loading ${config.title.toLowerCase()}...`}
          emptyText={`No ${config.title.toLowerCase()} found`}
          emptyHint="Try adjusting your search or add a new record"
          emptyIcon={<TitleIcon />}
          head={
            <>
              <Th>Actions</Th>
              {config.columns.map((column) => (
                <Th key={column.key}>{column.label}</Th>
              ))}
            </>
          }
        >
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50 transition-colors">
              <Cell>
                <div className="flex items-center gap-1">
                  <IconAction title="View" onClick={() => openView(row)}>
                    <LuEye />
                  </IconAction>
                  <IconAction title="Edit" tone="indigo" onClick={() => openEdit(row)}>
                    <FaEdit />
                  </IconAction>
                  <IconAction
                    title={row.is_active ? "Deactivate" : "Activate"}
                    tone={row.is_active ? "green" : "gray"}
                    disabled={actionLoading}
                    onClick={() => toggleStatus(row)}
                  >
                    {row.is_active ? <FaToggleOn /> : <FaToggleOff />}
                  </IconAction>
                  <IconAction
                    title="Delete"
                    tone="red"
                    onClick={() => {
                      setSelected(row)
                      setConfirmDelete(true)
                    }}
                  >
                    <FaTrash />
                  </IconAction>
                </div>
              </Cell>
              {config.columns.map((column) => (
                <Cell key={column.key}>{renderCell(row, column.key)}</Cell>
              ))}
            </tr>
          ))}
        </DataTable>
      </ListCard>

      {/* Add / Edit Drawer */}
      {(showAdd || showEdit) && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50" onClick={closeDrawer} />
          <div className="fixed top-0 right-0 h-full h-dvh w-full max-w-2xl bg-white shadow-xl z-50 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-200 bg-gray-50 shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{showEdit ? "Edit" : "Add"} {config.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{showEdit ? "Update the master record details" : "Create a new master record"}</p>
              </div>
              <button onClick={closeDrawer} disabled={actionLoading} className="p-2 hover:bg-gray-200 rounded transition-colors shrink-0">
                <FiX className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config.fields.map((field) => (
                    <div key={field.name} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t bg-gray-50 px-4 sm:px-6 py-4 shrink-0">
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    onClick={closeDrawer}
                    disabled={actionLoading}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitForm}
                    disabled={actionLoading}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading && <FaSpinner className="w-4 h-4 animate-spin" />}
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* View Drawer */}
      {showView && selected && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50" onClick={() => setShowView(false)} />
          <div className="fixed top-0 right-0 h-full h-dvh w-full max-w-2xl bg-white shadow-xl z-50 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-200 bg-gray-50 shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">View {config.title}</h3>
                <p className="text-sm text-gray-600 mt-1">Record details</p>
              </div>
              <button onClick={() => setShowView(false)} className="p-2 hover:bg-gray-200 rounded transition-colors shrink-0">
                <FiX className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-4">
              {config.fields.map((field) => {
                let displayValue: string
                if (field.type === "checkbox") {
                  displayValue = selected[field.name] ? "Yes" : "No"
                } else if (field.type === "select-search") {
                  const opt = buildInitialOption(selected, field)
                  displayValue = opt ? opt.label : "N/A"
                } else {
                  const v = selected[field.name]
                  displayValue = v === null || v === undefined || v === "" ? "N/A" : String(v)
                }
                return (
                  <div key={field.name} className="bg-gray-50 rounded border border-gray-200 p-4">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{field.label}</div>
                    <div className="mt-1 text-sm text-gray-900">{displayValue}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={deleteRow}
        title={`Delete ${config.title}`}
        message={`Are you sure you want to delete ${selected ? selected[config.primaryLabel] ?? "this record" : "this record"}?`}
        confirmText="Delete"
        variant="danger"
        loading={actionLoading}
      />
    </div>
  )
}
